import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  createAdminApiKey,
  deleteAdminApiKey,
  fetchAdminApiKeys,
} from '@/api/api-keys';
import { useTranslator } from '@/hooks/useTranslator';

interface Args {
  companyId: string;
  /** 1-based, as the table counts pages. */
  page: number;
  limit: number;
  filters?: Record<string, string[]>;
}

/**
 * This company's API keys, and the two writes beside them.
 *
 * The key itself comes back exactly once, from the create call — the list only
 * ever carries a masked value — so the caller is handed the created key to show
 * before it is gone.
 */
/** Shared by the tab's route loader and the hook. */
export function companyApiKeysQueryOptions({
  companyId,
  page,
  limit,
  filters,
}: Args) {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(filters ?? {})) {
    const value = values?.[0];
    if (value) filter[key] = value;
  }

  const params = new URLSearchParams({
    companyId,
    page: String(Math.max(0, page - 1)),
    count: String(limit),
  });
  if (Object.keys(filter).length > 0)
    params.set('filter', JSON.stringify(filter));
  const qs = params.toString();

  return queryOptions({
    queryKey: ['admin', 'api-keys', companyId, page, limit, filter] as const,
    queryFn: () => fetchAdminApiKeys(qs),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

export function useCompanyApiKeys(args: Args) {
  const { companyId } = args;
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const queryClient = useQueryClient();

  const query = useQuery(companyApiKeysQueryOptions(args));

  const createMutation = useMutation({ mutationFn: createAdminApiKey });
  const revokeMutation = useMutation({ mutationFn: deleteAdminApiKey });
  const [revokingLabel, setRevokingLabel] = useState<string | null>(null);

  function invalidate(): void {
    void queryClient.invalidateQueries({
      queryKey: ['admin', 'api-keys', companyId],
    });
  }

  /** Returns the created key, which is the only time its value is readable. */
  async function create(label: string): Promise<string | null> {
    try {
      const { data } = await createMutation.mutateAsync({ label, companyId });
      invalidate();
      return data.companyApiKey;
    } catch (error) {
      toast.error(t('errors.apiKeyCreateFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
      return null;
    }
  }

  async function revoke(label: string): Promise<void> {
    setRevokingLabel(label);
    try {
      await revokeMutation.mutateAsync({ label, companyId });
      toast.success(t('detail.apiKeys.revoked'));
      invalidate();
    } catch (error) {
      toast.error(t('errors.apiKeyRevokeFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setRevokingLabel(null);
    }
  }

  return {
    rows: query.data?.data ?? [],
    totalCount: query.data?.meta?.totalCount ?? query.data?.data?.length ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    create,
    isCreating: createMutation.isPending,
    revoke,
    revokingLabel,
  };
}
